import { DataTableColumnType } from '../../../common/_custom/DataTable/DataTable.types';
import {
    BLACK_DROPDOWN_CARET,
    BLUE_DROPDOWN_CARET,
    GREEN_DROPDOWN_CARET,
    GREY_DROPDOWN_CARET,
    ORANGE_DROPDOWN_CARET,
    PURPLE_DROPDOWN_CARET,
    RED_DROPDOWN_CARET,
    VOILET_DROPDOWN_CARET,
    YELLOW_DROPDOWN_CARET,
} from '../../../assets/hostedassets';

export const PLATFORM_CONFIG_TABS = {
    LEADS: 'leadsColumns',
    DEALS: 'dealViewHeaders',
    MANDATES: 'mandateHeaders',
    MANAGE_PARTNERS: 'managePartnersColumns',
    MANAGE_LENDERS: 'manageLendersColumns',
    MANAGEMENT_PROFILE: 'managementProfileColumns',
} as const;

export type PlatformConfigTabKey = (typeof PLATFORM_CONFIG_TABS)[keyof typeof PLATFORM_CONFIG_TABS];

export const TAB_LABELS: Record<PlatformConfigTabKey, string> = {
    leadsColumns: 'Leads',
    dealViewHeaders: 'Deals',
    mandateHeaders: 'Mandates',
    managePartnersColumns: 'Manage Partners',
    manageLendersColumns: 'Manage Lenders',
    managementProfileColumns: 'Management Profile',
};

export const TRACK_CHANGES_LABEL = 'Track changes';

export const DRAWER_MODE_EDIT = 'edit' as const;
export const DEFAULT_COLUMN_TYPE = DataTableColumnType.TEXT;
export const DEFAULT_ORG_ID = '0000000000000000' as const;

// Protected fields that are fully immutable
export const PROTECTED_FIELDS = new Set(['companyStatus', 'customFields.relationshipStatus']);

// Tabs where Lender persona is meaningful
export const LENDER_PERSONA_TABS = new Set<PlatformConfigTabKey>(['dealViewHeaders']);

// Admin-only pages — no persona grid rendered
export const ADMIN_ONLY_TABS = new Set<PlatformConfigTabKey>(['managePartnersColumns', 'manageLendersColumns', 'managementProfileColumns']);

// Tabs whose field values are change-recorded and whose grid renders a history icon
export const FIELD_HISTORY_TABS = new Set<PlatformConfigTabKey>([
    PLATFORM_CONFIG_TABS.LEADS,
    PLATFORM_CONFIG_TABS.DEALS,
    PLATFORM_CONFIG_TABS.MANDATES,
    PLATFORM_CONFIG_TABS.MANAGEMENT_PROFILE,
]);

// History tabs whose recorder audits every change unconditionally (UW) — only isHistoryEnabled applies
export const HISTORY_FLAG_ONLY_TABS = new Set<PlatformConfigTabKey>([PLATFORM_CONFIG_TABS.MANAGEMENT_PROFILE]);

// Column types whose value lives in customFields.<slug>, is user/API-written, and is diffed by the
// field recorders — Track changes applies only to these (plus OWNER, history-flag-only). Derived /
// action types (COMMENT, LAST_STATUS_UPDATE, GRADIENT_TEXT, EXPANDABLE, …) have no value of their own.
export const TRACKED_VALUE_COLUMN_TYPES = new Set<string>([
    DataTableColumnType.TEXT,
    DataTableColumnType.NUMBER,
    DataTableColumnType.DATE_FORMAT,
    DataTableColumnType.DATE_PICKER,
    DataTableColumnType.PILL,
    DataTableColumnType.SINGLE_SELECT_DROPDOWN,
    DataTableColumnType.MULTI_SELECT_DROPDOWN,
]);

// Column types offered by COLUMN_TYPE_OPTIONS (or the descriptor) that don't apply to a
// given tab's entity — excluded from that tab's type picker so an admin can't create a
// column the table can never render/save for that entity (G-5). OWNER has no
// backing concept for a director row (no owners bag, no assignment write path); GRADIENT_TEXT
// has no write path anywhere in the admin platform (system-composed display text only) —
// a custom field of either type on Management Profile would be permanently unpopulatable.
export const TAB_TYPE_EXCLUSIONS: Partial<Record<PlatformConfigTabKey, DataTableColumnType[]>> = {
    managementProfileColumns: [DataTableColumnType.OWNER, DataTableColumnType.GRADIENT_TEXT],
};

// Column number unit options
export const NUMBER_UNIT_OPTIONS = [
    { id: '', label: 'None' },
    { id: '₹', label: '₹' },
    { id: '%', label: '%' },
    { id: 'Cr', label: 'Cr' },
];

// Width unit for the width field (the UI hardcodes '%'; 'px' remains parseable from legacy configs)
export type WidthUnit = '%' | 'px';

// Preset color palette for dropdown options. Caret URLs come from hostedassets
// (single source — avoids drift with hand-built S3 paths).
export const OPTION_COLOR_PRESETS = [
    { id: 'grey', label: 'Grey', backgroundColor: '#F5F5F5', labelColor: '#4D4D4D', customCaret: GREY_DROPDOWN_CARET },
    {
        id: 'green',
        label: 'Green',
        backgroundColor: '#E7FFDB',
        labelColor: '#49A71B',
        customCaret: GREEN_DROPDOWN_CARET,
    },
    {
        id: 'yellow',
        label: 'Yellow',
        backgroundColor: '#FEF9EC',
        labelColor: '#E4AB01',
        customCaret: YELLOW_DROPDOWN_CARET,
    },
    {
        id: 'orange',
        label: 'Orange',
        backgroundColor: '#FFF0E7',
        labelColor: '#E17F66',
        customCaret: ORANGE_DROPDOWN_CARET,
    },
    { id: 'red', label: 'Red', backgroundColor: '#FFEEEE', labelColor: '#DF666A', customCaret: RED_DROPDOWN_CARET },
    { id: 'blue', label: 'Blue', backgroundColor: '#E2EFFF', labelColor: '#004CA2', customCaret: BLUE_DROPDOWN_CARET },
    {
        id: 'purple',
        label: 'Purple',
        backgroundColor: '#F2E8FF',
        labelColor: '#6021B3',
        customCaret: PURPLE_DROPDOWN_CARET,
    },
    {
        id: 'violet',
        label: 'Violet',
        backgroundColor: '#FDEBFF',
        labelColor: '#871494',
        customCaret: VOILET_DROPDOWN_CARET,
    },
    { id: 'teal', label: 'Teal', backgroundColor: '#E9FFFF', labelColor: '#054D49', customCaret: BLACK_DROPDOWN_CARET },
] as const;

export type ColorPresetId = (typeof OPTION_COLOR_PRESETS)[number]['id'] | 'custom';

// Grey preset caret is used as the fallback for custom-hex options
export const CUSTOM_COLOR_CARET = GREY_DROPDOWN_CARET;

// Column type labels for the type selector (9 types)
export const COLUMN_TYPE_OPTIONS = [
    { id: DataTableColumnType.TEXT, label: 'Text' },
    { id: DataTableColumnType.NUMBER, label: 'Number' },
    { id: DataTableColumnType.DATE_FORMAT, label: 'Date' },
    { id: DataTableColumnType.DATE_PICKER, label: 'Date Picker' },
    { id: DataTableColumnType.PILL, label: 'Pill' },
    { id: DataTableColumnType.SINGLE_SELECT_DROPDOWN, label: 'Single Select Dropdown' },
    { id: DataTableColumnType.MULTI_SELECT_DROPDOWN, label: 'Multi Select Dropdown' },
    { id: DataTableColumnType.OWNER, label: 'Owner' },
    { id: 'GRADIENT_TEXT', label: 'Gradient Text' },
];

// Filter type options
export const FILTER_TYPE_OPTIONS = [
    { id: 'MULTI_SELECT', label: 'Multi Select' },
    { id: 'RANGE', label: 'Range' },
    { id: 'DATE_RANGE', label: 'Date Range' },
];

// Alignment options
export const ALIGNMENT_OPTIONS = [
    { id: 'left', label: 'Left' },
    { id: 'center', label: 'Center' },
    { id: 'right', label: 'Right' },
];
